import React from 'react'

// Decorative inline SVGs for the "Why Choose USABG?" section. Every graphic is
// aria-hidden except the USABG wordmark, which carries an accessible name.

const svgProps = {
  xmlns: 'http://www.w3.org/2000/svg',
  'aria-hidden': 'true',
  focusable: 'false',
}

const strokeProps = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
}

export const GrowthIcon = () => (
  <svg {...svgProps} viewBox='0 0 24 24'>
    <g fill='currentColor'>
      <rect x='3.5' y='14' width='3.5' height='7' rx='0.6' />
      <rect x='9' y='11' width='3.5' height='10' rx='0.6' />
      <rect x='14.5' y='8' width='3.5' height='13' rx='0.6' />
    </g>
    <path {...strokeProps} d='M3.5 10.5 9 6l3 2.5L19.5 3' />
    <path {...strokeProps} d='M15.5 3h4v4' />
  </svg>
)

export const DollarIcon = () => (
  <svg {...svgProps} viewBox='0 0 24 24'>
    <path
      {...strokeProps}
      strokeWidth={2.4}
      d='M12 2v20M16.8 6.4C15.9 5 14.1 4.2 12 4.2c-2.9 0-4.8 1.5-4.8 3.6 0 2.4 2.2 3.2 4.8 3.9 2.7.7 5 1.6 5 4.2 0 2.2-2.1 3.9-5 3.9-2.4 0-4.4-1-5.3-2.6'
    />
  </svg>
)

export const BoxIcon = () => (
  <svg {...svgProps} viewBox='0 0 24 24'>
    <g {...strokeProps}>
      <path d='M12 2.5 3.5 7v10L12 21.5 20.5 17V7z' />
      <path d='M3.5 7 12 11.5 20.5 7M12 11.5v10' />
    </g>
  </svg>
)

export const ContractIcon = () => (
  <svg {...svgProps} viewBox='0 0 24 24'>
    <g {...strokeProps}>
      <path d='M14 2.5H6.5a2 2 0 0 0-2 2v15a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V8z' />
      <path d='M14 2.5V8h5.5M8 11h8M8 14.5h5' />
      <path d='M8 18.5c.8-1.2 1.6-1.2 2.2 0s1.4 1.2 2.2 0 1.6-1.2 2.4 0' />
    </g>
  </svg>
)

export const StarIcon = () => (
  <svg {...svgProps} viewBox='0 0 24 24'>
    <path fill='currentColor' d='m12 2.3 2.9 6.1 6.6.8-4.9 4.5 1.3 6.6L12 17l-5.9 3.3 1.3-6.6-4.9-4.5 6.6-.8z' />
  </svg>
)

export const TargetIcon = () => (
  <svg {...svgProps} viewBox='0 0 24 24'>
    <g {...strokeProps}>
      <circle cx='11' cy='13' r='8.5' />
      <circle cx='11' cy='13' r='5' />
      <circle cx='11' cy='13' r='1.5' fill='currentColor' />
      <path d='M11 13 20 4M16.5 4H20v3.5' />
    </g>
  </svg>
)

export const TrophyIcon = () => (
  <svg {...svgProps} viewBox='0 0 24 24'>
    <g {...strokeProps}>
      <path d='M7 3h10v5.5a5 5 0 0 1-10 0z' />
      <path d='M7 5H3.8v1.4A3.6 3.6 0 0 0 7.4 10M17 5h3.2v1.4A3.6 3.6 0 0 1 16.6 10' />
      <path d='M12 13.5V17M8.5 21h7M9.5 17h5l.5 4H9z' />
    </g>
  </svg>
)

export const PlaneIcon = () => (
  <svg {...svgProps} viewBox='0 0 24 24'>
    <path
      fill='currentColor'
      transform='rotate(90 12 12)'
      d='M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z'
    />
  </svg>
)

export const ChevronIcon = () => (
  <svg {...svgProps} viewBox='0 0 16 16'>
    <path {...strokeProps} strokeWidth={2.2} d='m6 3 5 5-5 5' />
  </svg>
)

export const ArrowIcon = () => (
  <svg {...svgProps} viewBox='0 0 16 16'>
    <path {...strokeProps} strokeWidth={2} d='M2.5 8h11M9 3.5 13.5 8 9 12.5' />
  </svg>
)

// USABG wordmark — the A is a solid peak with a star knocked out of it.
export const UsabgLogo = ({ className }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    className={className}
    viewBox='0 0 236 60'
    role='img'
    aria-label='USABG'
    focusable='false'
  >
    <g fill='currentColor'>
      <text
        x='0'
        y='54'
        textLength='84'
        lengthAdjust='spacingAndGlyphs'
        fontFamily='Outfit, Arial, sans-serif'
        fontWeight='800'
        fontSize='64'
      >
        US
      </text>
      <path
        fillRule='evenodd'
        d='M87 54 104.5 5h17L139 54h-14.5l-3-9.5h-17l-3 9.5zM113 15.5l2.6 5.5 6 .7-4.5 4.1 1.2 6-5.3-3-5.3 3 1.2-6-4.5-4.1 6-.7z'
      />
      <text
        x='142'
        y='54'
        textLength='94'
        lengthAdjust='spacingAndGlyphs'
        fontFamily='Outfit, Arial, sans-serif'
        fontWeight='800'
        fontSize='64'
      >
        BG
      </text>
    </g>
  </svg>
)

// Red / white / blue brush stripes for the top-left corner, drawn as crisp
// vector shapes. The bottom-right corner reuses it flipped in CSS.
export const CornerStripes = ({ className }) => (
  <svg {...svgProps} className={className} viewBox='0 0 360 240' preserveAspectRatio='xMinYMin meet'>
    <g>
      <path
        fill='#d6202a'
        d='M-69.3 135.6 L-58.6 130.1 L-49.1 122.7 L-39.5 115.5 L-28.8 109.9 L-19.5 102.2 L-9.4 95.7 L1.4 90.4 L11.9 84.4 L21.5 77.2 L30.9 69.7 L41.5 63.9 L51.1 56.7 L61.0 49.8 L71.6 44.1 L81.7 37.7 L91.8 31.2 L101.8 24.6 L112.5 19.1 L122.4 12.2 L131.8 4.7 L141.8 -1.9 L152.5 -7.5 L162.7 -13.9 L172.9 -20.2 L181.7 -28.7 L192.9 -33.4 L203.1 -39.8 L212.0 -48.1 L223.0 -53.2 L232.1 -61.2 L238.1 -55.7 L246.7 -53.2 L242.1 -42.1 L253.9 -41.7 L251.0 -32.1 L240.8 -25.7 L231.2 -18.5 L220.9 -12.4 L210.9 -5.7 L200.5 0.3 L191.2 8.1 L180.6 13.7 L170.5 20.3 L160.0 26.1 L150.2 33.0 L140.7 40.5 L131.1 47.6 L120.1 52.7 L110.5 60.0 L100.4 66.5 L90.6 73.4 L80.0 79.2 L69.7 85.3 L59.4 91.5 L49.3 97.9 L40.0 105.7 L30.4 112.9 L19.1 117.6 L10.0 125.7 L-0.7 131.2 L-10.1 138.7 L-20.5 144.7 L-30.5 151.4 L-40.7 157.7 L-50.7 164.4 L-70.2 168.8 L-60.3 154.3 L-74.4 155.3 L-65.9 141.7z'
      />
      <path
        fill='#ffffff'
        d='M-64.2 179.5 L-54.0 172.7 L-43.8 166.0 L-33.3 159.5 L-22.1 154.4 L-12.7 146.4 L-2.5 139.5 L8.6 134.1 L17.9 125.9 L28.3 119.5 L39.3 113.9 L49.0 106.3 L59.0 99.2 L69.5 92.8 L79.9 86.4 L91.0 80.9 L100.8 73.5 L110.5 66.0 L121.8 60.8 L131.2 52.8 L142.1 47.1 L151.7 39.3 L162.9 34.0 L172.5 26.4 L182.3 19.0 L192.9 12.7 L203.2 6.1 L213.9 0.1 L223.6 -7.5 L233.8 -14.3 L244.7 -20.0 L255.0 -26.6 L254.4 -23.2 L263.2 -25.5 L257.9 -18.8 L272.0 -24.6 L262.2 -15.5 L252.7 -7.7 L241.6 -2.3 L232.0 5.4 L221.1 11.2 L211.5 18.9 L201.1 25.4 L191.1 32.6 L179.7 37.5 L170.1 45.2 L160.3 52.6 L150.0 59.2 L138.7 64.4 L129.3 72.3 L118.1 77.7 L108.8 85.8 L98.0 91.7 L87.7 98.3 L77.6 105.3 L67.3 111.9 L56.3 117.6 L46.4 124.8 L36.0 131.3 L26.7 139.4 L16.0 145.4 L5.4 151.6 L-4.3 159.3 L-14.7 165.8 L-25.6 171.4 L-35.7 178.4 L-45.7 185.5 L-55.9 192.3 L-65.1 194.3 L-60.1 187.7 L-73.5 193.1 L-64.8 184.1z'
      />
      <path
        fill='#1d4f9c'
        d='M-72.2 203.3 L-61.3 198.0 L-51.6 190.9 L-42.3 183.2 L-32.4 176.4 L-21.4 171.3 L-12.2 163.4 L-1.6 157.8 L8.3 150.9 L18.0 143.9 L29.0 138.7 L39.0 132.1 L49.2 125.7 L58.7 118.3 L69.4 112.9 L78.9 105.3 L89.5 99.7 L99.3 92.7 L109.3 86.1 L118.7 78.5 L129.9 73.8 L139.7 66.8 L149.7 60.2 L159.4 53.1 L169.0 45.9 L179.0 39.2 L189.7 33.6 L200.0 27.5 L210.0 20.8 L219.6 13.5 L229.7 7.1 L239.6 0.3 L250.6 -4.8 L260.1 -12.2 L270.0 -19.0 L279.9 -25.8 L287.8 -19.6 L306.7 -21.4 L293.9 -2.6 L312.3 -4.0 L304.1 11.6 L294.4 18.6 L284.6 25.6 L274.2 31.6 L263.5 37.2 L254.1 44.7 L243.2 49.9 L234.3 58.3 L223.7 64.0 L213.3 70.0 L203.7 77.3 L193.8 84.1 L183.9 90.8 L173.5 96.8 L163.2 103.0 L153.8 110.5 L143.4 116.6 L133.3 123.0 L122.9 129.1 L113.1 136.0 L103.3 143.0 L93.4 149.7 L83.1 155.9 L73.3 162.9 L63.0 169.0 L53.1 175.9 L42.6 181.7 L33.1 189.2 L22.7 195.2 L12.5 201.5 L2.9 208.7 L-7.2 215.1 L-18.4 219.9 L-27.8 227.5 L-37.7 234.2 L-47.5 241.2 L-59.7 237.5 L-57.6 225.7 L-78.7 228.9 L-64.8 209.4z'
      />
      <path
        fill='#8fb3e3'
        opacity='0.6'
        d='M-43.0 263.4 L-33.3 255.9 L-22.8 249.6 L-12.9 242.4 L-2.1 236.6 L7.7 229.3 L18.9 224.0 L27.9 215.5 L39.2 210.5 L49.3 203.5 L59.0 196.0 L68.9 188.8 L79.7 183.0 L90.4 177.1 L99.8 169.1 L110.7 163.4 L120.9 156.7 L131.4 150.4 L141.5 143.5 L151.5 136.4 L161.1 128.8 L172.3 123.6 L181.9 116.1 L192.7 110.3 L202.9 103.4 L212.9 96.3 L223.8 90.7 L234.1 84.1 L243.3 75.8 L254.2 70.2 L264.1 63.0 L275.0 57.4 L285.3 50.8 L295.2 43.6 L305.5 37.1 L315.7 30.3 L325.4 22.7 L330.3 23.3 L340.6 19.4 L331.6 28.1 L340.3 25.3 L332.8 34.2 L322.3 40.5 L312.2 47.3 L301.1 52.8 L291.2 59.9 L281.4 67.3 L270.9 73.5 L261.1 80.8 L250.2 86.6 L240.3 93.7 L229.2 99.1 L219.9 107.2 L209.6 113.8 L199.1 120.0 L188.6 126.3 L178.1 132.6 L168.2 139.8 L157.6 145.9 L147.4 152.6 L138.1 160.8 L127.5 166.8 L117.0 173.1 L106.2 178.9 L96.2 186.0 L85.7 192.2 L75.5 199.1 L66.2 207.1 L55.6 213.3 L44.7 218.9 L34.4 225.5 L24.7 233.0 L15.0 240.4 L4.3 246.4 L-6.1 252.9 L-16.5 259.3 L-26.9 265.7 L-36.1 273.9 L-54.6 281.7 L-40.4 269.7 L-46.8 271.0 L-42.4 265.2z'
      />
    </g>
  </svg>
)

// Navy brush-stroke band behind the "Real People. Real Opportunities." banner.
export const BrushBand = ({ className, filterId }) => (
  <svg {...svgProps} className={className} viewBox='0 0 800 150' preserveAspectRatio='none'>
    <defs>
      <filter id={filterId} x='-5%' y='-20%' width='110%' height='140%'>
        <feTurbulence type='fractalNoise' baseFrequency='0.012 0.09' numOctaves='3' seed='3' />
        <feDisplacementMap in='SourceGraphic' scale='22' />
      </filter>
    </defs>
    <g filter={`url(#${filterId})`} fill='#10285a'>
      <path d='M38 30 C180 14 520 10 772 22 L760 52 L792 70 L756 92 L782 118 C560 136 240 140 30 128 L52 104 L14 84 L48 62 L8 44 z' />
      <path opacity='0.85' d='M0 66 L70 58 L64 74 z M800 82 L728 74 L736 92 z' />
    </g>
  </svg>
)
